import { computed } from 'vue';

import { GlobalState } from '@/global';

// Pure scope logic, same pattern as sidebar/location-status.ts — kept independent of the dock/modal
// so both can filter on exactly the same rules (see docs/parking-lot-location-scope-plan.md §0).

/**
 * Where parking lots are visible from here. `pending` is a workstation whose config hasn't loaded
 * yet (first boot / failed load) — it shows nothing rather than flashing another site's lots.
 */
export type ParkingScope = { kind: 'all' } | { kind: 'location'; locationId: string } | { kind: 'pending' };

export function parkingScope(isWorkstation: boolean, config: { 'workstation.locationId'?: string } | null): ParkingScope {
    if (!isWorkstation) return { kind: 'all' };
    // Loading / failed load — hide rather than flash other sites' lots.
    if (!config) return { kind: 'pending' };
    const locationId = config['workstation.locationId'];
    // Personal workstation (no location) → browser behavior.
    return locationId ? { kind: 'location', locationId } : { kind: 'all' };
}

/** Whether `lot` shows under `scope`. A lot with no location (or an old API that omits it) is tenant-wide. */
export function isLotInScope(lot: { locationId?: string | null }, scope: ParkingScope): boolean {
    if (scope.kind === 'pending') return false;
    return scope.kind === 'all' || !lot.locationId || lot.locationId === scope.locationId;
}

/** Shared by the sidebar dock and the Parking modal, so both filter on one source. */
export function useParkingScope() {
    return computed(() => parkingScope(GlobalState.isWorkstation, GlobalState.tenantConfiguration.value));
}
